import {
  ApolloClient,
  InMemoryCache,
  ApolloLink,
  HttpLink,
  Observable,
} from "@apollo/client";
import { setContext } from "@apollo/client/link/context";
import { onError } from "@apollo/client/link/error";
import { createUploadLink } from "apollo-upload-client";
import { decryptingFetch } from "../utils/encryption";

const API_URL = process.env.REACT_APP_API_URL as string;

const LOGOUT_MARKER_COOKIE = "ug_logout_sync";
const AUTH_STORAGE_KEYS = [
  "rememberMe",
  "token",
  "user",
  "isLoggedIn",
  "ug_cart",
  "ug_pending_cart_checkout",
  "user_session",
  "ug_user",
  "refreshToken",
] as const;

const getCookie = (name: string): string | null => {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop()?.split(";").shift() ?? null;
  return null;
};

const getLegacySharedDomain = (): string | undefined => {
  const host = window.location.hostname;
  if (host === "localhost" || host === "127.0.0.1") {
    return undefined;
  }
  const parts = host.split(".");
  if (parts.length < 2) {
    return undefined;
  }
  return `.${parts.slice(-2).join(".")}`;
};

const getSecureCookieSuffix = () =>
window.location.protocol === "https:" ? "; Secure" : "";
const hasLogoutMarker = () => getCookie(LOGOUT_MARKER_COOKIE) === "1";
const clearAuthState = () => {
  AUTH_STORAGE_KEYS.forEach((key) => {
    localStorage.removeItem(key);
    sessionStorage.removeItem(key);
  });
};
const clearLogoutMarker = () => {
  const secure = getSecureCookieSuffix();
  document.cookie = `${LOGOUT_MARKER_COOKIE}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; SameSite=Lax${secure};`;
  const legacyDomain = getLegacySharedDomain();
  if (legacyDomain) {
    document.cookie = `${LOGOUT_MARKER_COOKIE}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; SameSite=Lax${secure}; domain=${legacyDomain};`;
  }
};

const uploadLink = createUploadLink({
  uri: API_URL,
  credentials: "include",
  fetch: decryptingFetch as any,
});

// ─── Token helpers ───────────────────────────────────────────────────────────

/** Decode JWT payload without a library (works in all browsers). */
function decodeTokenPayload(token: string): any | null {
  try {
    const base64 = token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/");
    return JSON.parse(atob(base64));
  } catch {
    return null;
  }
}

/** Returns true when the token will expire within the given buffer (ms). */
function isTokenExpiringSoon(token: string | null, bufferMs = 5 * 60 * 1000): boolean {
  if (!token) return true;
  const payload = decodeTokenPayload(token);
  if (!payload?.exp) return false; // can't tell → let the server decide
  return payload.exp * 1000 - Date.now() < bufferMs;
}

/** Persist a fresh access token in both storage layers. */
function persistToken(token: string) {
  clearLogoutMarker();
  localStorage.setItem("token", token);
  sessionStorage.setItem("token", token);
}

function getStoredToken(): string | null {
  if (hasLogoutMarker()) {
    clearAuthState();
    return null;
  }

  return localStorage.getItem("token") || sessionStorage.getItem("token");
}

// ─── Session cleanup ─────────────────────────────────────────────────────────

const handleInvalidSession = () => {
  console.warn("Session expired — redirecting to login");
  clearAuthState();
  window.location.href = "/";
};

// ─── Silent refresh ──────────────────────────────────────────────────────────

let isRefreshing = false;
let pendingRequests: Array<(token: string | null) => void> = [];

const resolvePendingRequests = (token: string | null) => {
  pendingRequests.forEach((cb) => cb(token));
  pendingRequests = [];
};

/**
 * Call the refreshToken mutation using plain fetch so it is independent of
 * the Apollo link chain.  `credentials: "include"` sends the httpOnly
 * refresh-token cookie the server needs.
 */
function doRefresh(): Promise<string | null> {
  if (hasLogoutMarker()) {
    clearAuthState();
    return Promise.resolve(null);
  }

  return decryptingFetch(API_URL, {
    method: "POST",
    credentials: "include",               // ← sends the refreshToken cookie
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      query: `mutation RefreshToken { refreshToken { success token } }`,
    }),
  })
    .then((res) => res.json())
    .then((result) => {
      const data = result.data;
      if (data?.refreshToken?.success && data.refreshToken.token) {
        persistToken(data.refreshToken.token);
        return data.refreshToken.token as string;
      }
      return null;
    })
    .catch(() => null);
}

// ─── Proactive refresh timer ─────────────────────────────────────────────────
// Refresh the token 5 minutes before it expires so the user never sees a 401.

let refreshTimer: ReturnType<typeof setTimeout> | null = null;

function scheduleProactiveRefresh() {
  if (refreshTimer) clearTimeout(refreshTimer);

  const token = getStoredToken();
  if (!token) return;

  const payload = decodeTokenPayload(token);
  if (!payload?.exp) return;

  // Refresh 5 minutes before expiry, minimum 10 seconds from now
  const msUntilExpiry = payload.exp * 1000 - Date.now();
  const delay = Math.max(msUntilExpiry - 5 * 60 * 1000, 10_000);

  refreshTimer = setTimeout(async () => {
    if (isRefreshing) return;
    isRefreshing = true;
    const newToken = await doRefresh();
    isRefreshing = false;

    if (newToken) {
      scheduleProactiveRefresh(); // schedule next refresh
    } else {
      handleInvalidSession();
    }
  }, delay);
}

// Kick off the first schedule if there is already a token (page reload / tab restore)
scheduleProactiveRefresh();

// ─── Auth link ───────────────────────────────────────────────────────────────

const authLink = setContext(async (_, { headers }) => {
  let token = getStoredToken();

  // If the token is about to expire, try a silent refresh before the request
  if (isTokenExpiringSoon(token) && !isRefreshing) {
    isRefreshing = true;
    const newToken = await doRefresh();
    isRefreshing = false;
    if (newToken) {
      token = newToken;
    }
  }

  // Always ensure the proactive-refresh timer is running (idempotent & cheap)
  scheduleProactiveRefresh();

  return {
    headers: {
      ...headers,
      ...(token ? { authorization: `Bearer ${token}` } : {}),
    },
  };
});

// ─── Error link (fallback 401 handler) ───────────────────────────────────────

const isUnauthorizedStatus = (status?: number, message?: string) =>
  status === 401 ||
  status === 403 ||
  Boolean(message && (message.includes("401") || message.includes("403")));

const errorLink = onError(({ graphQLErrors, networkError, operation, forward }) => {
  if (graphQLErrors) {
    for (const err of graphQLErrors) {
      const code = (err.extensions as any)?.code;
      const status = (err.extensions as any)?.response?.status;
      const message = err.message;

      if (code === "UNAUTHENTICATED" || isUnauthorizedStatus(status, message)) {
        if (operation.operationName === "RefreshToken") {
          handleInvalidSession();
          return;
        }

        if (!isRefreshing) {
          isRefreshing = true;

          doRefresh()
            .then((newToken) => {
              if (newToken) {
                scheduleProactiveRefresh();
                resolvePendingRequests(newToken);
              } else {
                resolvePendingRequests(null);
                handleInvalidSession();
              }
            })
            .finally(() => {
              isRefreshing = false;
            });
        }

        return new Observable((observer) => {
          pendingRequests.push((token: string | null) => {
            if (token) {
              operation.setContext(({ headers = {} }: any) => ({
                headers: {
                  ...headers,
                  authorization: `Bearer ${token}`,
                },
              }));
              const subscriber = forward(operation).subscribe({
                next: observer.next.bind(observer),
                error: observer.error.bind(observer),
                complete: observer.complete.bind(observer),
              });
              return () => subscriber.unsubscribe();
            } else {
              observer.error(new Error("Refresh failed"));
            }
          });
        }) as any;
      }
    }
  }

  if (networkError) {
    const status = (networkError as any)?.statusCode;
    const message = (networkError as any)?.message;
    if (isUnauthorizedStatus(status, message)) {
      handleInvalidSession();
    }
    console.error("[Network error]:", networkError);
  }
});

// ─── Client ──────────────────────────────────────────────────────────────────

export const client = new ApolloClient({
  link: ApolloLink.from([errorLink, authLink, uploadLink]),
  cache: new InMemoryCache(),
});
